import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";

async function safeCopyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to execCommand fallback
  }
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    textarea.style.pointerEvents = "none";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

interface UseWalkthroughShareOptions {
  walkthroughId: string | undefined;
  isValidId: boolean;
}

export function useWalkthroughShare({
  walkthroughId,
  isValidId,
}: UseWalkthroughShareOptions) {
  const [shareInfo, setShareInfo] = useState<{
    enabled: boolean;
    shareToken: string | null;
    shareUrl: string | null;
  } | null>(null);
  const [isSharing, setIsSharing] = useState(false);

  useEffect(() => {
    if (!isValidId || !walkthroughId) return;
    fetch(`/api/walkthroughs/${walkthroughId}/share`, {
      credentials: "include",
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) {
          const url = data.shareToken
            ? `${window.location.origin}/share/reports/${data.shareToken}`
            : data.shareUrl;
          setShareInfo({ ...data, shareUrl: url });
        }
      })
      .catch(() => {});
  }, [isValidId, walkthroughId]);

  const handleDownloadPdf = useCallback(async () => {
    if (!walkthroughId) return;
    try {
      toast.info("Generating PDF report…");
      const res = await fetch(`/api/walkthroughs/${walkthroughId}/pdf`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to generate PDF (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `walkthrough-${walkthroughId.slice(0, 8)}-report.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF report downloaded");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to download PDF",
      );
    }
  }, [walkthroughId]);

  const handleDownloadHtml = useCallback(async () => {
    if (!walkthroughId) return;
    try {
      toast.info("Downloading HTML report…");
      const res = await fetch(`/api/walkthroughs/${walkthroughId}/html`, {
        credentials: "include",
      });
      if (!res.ok)
        throw new Error(`Failed to download HTML report (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `walkthrough-${walkthroughId.slice(0, 8)}-report.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("HTML report downloaded");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to download HTML report",
      );
    }
  }, [walkthroughId]);

  const handleSharePublicLink = useCallback(async () => {
    if (!walkthroughId) return;
    try {
      setIsSharing(true);
      const res = await fetch(`/api/walkthroughs/${walkthroughId}/share`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok)
        throw new Error(`Failed to generate share link (${res.status})`);
      const data = await res.json();
      const url = data.shareToken
        ? `${window.location.origin}/share/reports/${data.shareToken}`
        : data.shareUrl;
      setShareInfo({ ...data, shareUrl: url });
      if (url) {
        const copied = await safeCopyToClipboard(url);
        if (copied) {
          toast.success("Public report link copied to clipboard");
        } else {
          toast.success("Public report link generated", {
            description: url,
          });
        }
      }
    } catch {
      toast.error("Failed to generate share link");
    } finally {
      setIsSharing(false);
    }
  }, [walkthroughId]);

  const handleRevokeShareLink = useCallback(async () => {
    if (!walkthroughId) return;
    try {
      setIsSharing(true);
      const res = await fetch(
        `/api/walkthroughs/${walkthroughId}/share/revoke`,
        {
          method: "POST",
          credentials: "include",
        },
      );
      if (!res.ok)
        throw new Error(`Failed to revoke share link (${res.status})`);
      setShareInfo((prev) => (prev ? { ...prev, enabled: false } : null));
      toast.success("Public report link revoked");
    } catch {
      toast.error("Failed to revoke share link");
    } finally {
      setIsSharing(false);
    }
  }, [walkthroughId]);

  const handleCopyLink = useCallback(async () => {
    const copied = await safeCopyToClipboard(window.location.href);
    if (copied) {
      toast.success("Walkthrough link copied to clipboard");
    } else {
      toast.error("Clipboard access was denied by the browser.");
    }
  }, []);

  return {
    shareInfo,
    isSharing,
    handleDownloadPdf,
    handleDownloadHtml,
    handleSharePublicLink,
    handleRevokeShareLink,
    handleCopyLink,
  };
}
